import type { SearchAsset } from '@/api/searchAssets';

/**
 * Content signature of the SearchAsset fields the load / validation-restore
 * effects in SearchOrchestrator actually read.
 *
 * Both effects used to depend on the `assetForVariant` object identity. Since
 * that object is recreated by every refetch of ['search-assets'], ANY
 * invalidation (approve, save, validate, delete-version, campaign-goes-live)
 * re-ran them in full — even though approve only writes
 * status/approved_by_user_id/approved_at/approval_note.
 *
 * That was expensive for two reasons:
 *  1. The load effect re-`dispatch`es a full form reset from the server.
 *  2. It then re-downloads EVERY saved media file — it builds a fresh
 *     `new Image()` / `<video preload="metadata">` per item purely to detect
 *     dimensions. The backend mints a new signed URL per request, so none of
 *     that can be served from the browser cache.
 *
 * This is the search-side counterpart of `assetsContentSignature` (social).
 * Keep the two in sync in spirit — but note they deliberately differ on
 * `validation_result`, see below.
 *
 * Deliberately EXCLUDED:
 *  - `signed_url` (asset-level and per media file) — regenerated on every
 *    request, so including it would defeat the whole optimisation. It is only
 *    read to point an <img>/<video> at, never compared.
 *  - `status` / `approved_at` / `approved_by_user_id` / `approval_note` —
 *    written by approve, never read by these effects.
 *  - `updated_at` — bumped by every write, so it would also defeat the point.
 *  - `is_locked` — read at render time (line ~315), not inside these effects.
 *
 * Deliberately INCLUDED, unlike the social signature:
 *  - `validation_result` — the restore effect exists specifically to map this
 *    into fieldIssues. If it were excluded, validation errors would stop
 *    appearing after a save/validate round-trip.
 *  - `is_offer_led` / `offer_end_date` — read by assetToFormState, and the
 *    backend nulls offer_end_date whenever is_offer_led is false, so an
 *    offer-only round-trip must re-run the load effect.
 *
 * Lives in its own module so it can be unit-tested without importing
 * SearchOrchestrator's heavy dependencies.
 */
export function searchAssetContentSignature(asset: SearchAsset | undefined): string {
  if (!asset) return '';
  return JSON.stringify([
    asset.id,
    asset.variant_number,
    // Text fields read by assetToFormState
    asset.headlines,
    asset.long_headlines,
    asset.descriptions,
    asset.display_url_paths,
    asset.cta,
    asset.final_url,
    asset.business_name,
    asset.legal,
    asset.sitelinks,
    asset.is_offer_led,
    asset.offer_end_date,
    // Media, minus the per-request signed URL
    (asset.media_files ?? []).map((m) => [
      m.id,
      m.slot,
      m.storage_path,
      m.content_type,
      m.file_size,
      m.source_fordshare_id,
      m.expiration_date,
      m.width,
      m.height,
    ]),
    // Drives the validation-restore effect — must be part of the key
    asset.validation_result,
  ]);
}
